import { DatePicker, TimePicker } from "antd/es";
import dayjs from "dayjs";
import { Guid } from "guid-typescript";
import _ from "lodash";
import { PickerSharedProps } from "rc-picker/es/Picker";
import { PureComponent, ReactNode } from "react";

interface IKDatatimePickerProps extends PickerSharedProps<any> {
  value?: string;
  defaultValue?: string;
  format?: string;
  picker?: "time" | "date" | "week" | "month" | "quarter" | "year";
  display?: "Date" | "Time" | "Date & Time";
  showHour?: boolean;
  showMinute?: boolean;
  showSecond?: boolean;
  onChange?: (value: string) => void;
  style?: any;
  placeholder?: string;
  className?: string;
}

interface IKDatatimePickerState {
  uniqueKey: Guid;
}

declare let window: Window & { kuika: any };

export class KDatatimePicker extends PureComponent<IKDatatimePickerProps, IKDatatimePickerState> {
  private memoizedDynamicCssResult = "";

  constructor(props: IKDatatimePickerProps) {
    super(props);
    this.state = {
      uniqueKey: Guid.create()
    };
  }

  componentDidMount = () => {
    this.setDynamicStyle();
  };

  setDynamicStyle = () => {
    const uniquekey = this.state.uniqueKey?.toString();
    if (!uniquekey) {
      return;
    }
    const isDesignTime = window.kuika?.isDesignTime;
    if (this.memoizedDynamicCssResult !== "" && !isDesignTime) {
      return this.memoizedDynamicCssResult;
    }
    const dynamic_style = document.getElementById("dynamic_style");
    if (dynamic_style && dynamic_style.innerHTML?.indexOf(uniquekey) === -1) {
      const generatedCss = this.getDynamicCss();
      dynamic_style.innerHTML = `${dynamic_style.innerHTML}
        ${generatedCss}`;
      this.memoizedDynamicCssResult = generatedCss;
    }
  };

  getDynamicCss = (): string => {
    const className: string = this.getClassName();
    if (!className || className.length === 0) {
      return "";
    }

    let result = "";
    if (this.props.style?.color?.length > 0) {
      result +=
        `.${className.trim()} .ant-picker-input>input {
          ` +
        `color: ${this.props.style?.color}
        }`;
    }

    if (this.props.style?.backgroundColor?.length > 0 || this.props.style?.color?.length > 0) {
      result += `
          .${className.trim()} .ant-picker-clear {
            `;
      if (this.props.style?.backgroundColor?.length > 0)
        result += `background: ${this.props.style?.backgroundColor} !important;`;
      if (this.props.style?.color?.length > 0) result += `color: ${this.props.style?.color} !important;`;
      result += `;
      }`;
    }
    return result;
  };

  getClassName = () => {
    let result = "";
    if (this.props.className) {
      result = this.props.className;
    }
    if (!this.state.uniqueKey) {
      return result;
    }
    result = `${result} kdatepicker_${this.state.uniqueKey.toString().substring(0, 8)}`;
    return result;
  };

  convertToDayjs = (value: string) => {
    return dayjs(value, this.getFormat());
  };

  convertToString = (value: dayjs.Dayjs) => {
    const result = value?.format(this.getFormat());
    return result;
  };

  getFormat = () => {
    if (this.props.format && this.props.format.length > 0) {
      return this.props.format;
    }
    if (this.props.display === undefined || this.props.display === "Date") {
      return "YYYY-MM-DD";
    }
    return "YYYY-MM-DDTHH:mm:ss";
  };

  handleChange = (value: any) => {
    const dateValue: any = this.convertToString(value);
    if (this.props.onChange) {
      this.props.onChange(dateValue);
    }
  };

  getGenericValue = () => {
    let result: any = {};
    if (this.props.value) {
      result = { value: this.convertToDayjs(this.props.value) };
    }
    return result;
  };

  getGenericDefaultValue = () => {
    let result: any = {};
    if (this.props.defaultValue) {
      result = { defaultValue: this.convertToDayjs(this.props.defaultValue) };
    }
    return result;
  };

  getProps = () => {
    const props: any = _.clone(this.props);
    let style: any = _.clone(this.props.style);
    if (props.defaultValue) {
      delete props.defaultValue;
    }
    if (props.value) {
      delete props.value;
    }
    if (props.display) {
      delete props.display;
    }
    if (!style) {
      style = {
        width: "100%"
      };
    } else if (!style.width) {
      style.width = "100%";
    }
    props.style = style;
    return props;
  };

  getDisplay = () => {
    if (this.props.display) {
      return this.props.display;
    }
    return "Date";
  };

  render(): ReactNode {
    const display = this.getDisplay();
    return (
      <>
        {(display === "Date" || display === "Date & Time") && (
          <DatePicker
            {...this.getProps()}
            className={this.getClassName()}
            showTime={display === "Date & Time"}
            {...this.getGenericValue()}
            {...this.getGenericDefaultValue()}
            onChange={this.handleChange}
          ></DatePicker>
        )}
        {display === "Time" && (
          <TimePicker
            {...this.getProps()}
            className={this.getClassName()}
            {...this.getGenericValue()}
            {...this.getGenericDefaultValue()}
            onChange={this.handleChange}
          ></TimePicker>
        )}
      </>
    );
  }
}
